/**
 * Shared hook for employee data management
 * Used by Owner and Manager screens
 */

import { collection, query, where, getDocs, QueryConstraint } from 'firebase/firestore';
import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';

import { firestore } from '@/lib/firebase/client';


export interface Employee {
  id: string;
  name: string;
  email: string;
  phone?: string;
  salaryType: 'hourly' | 'salary';
  hourlyRate?: number;
  monthlySalary?: number;
  expectedHours?: number;
  locationIds: string[];
  active: boolean;
  isManager: boolean;
  managerLocationIds?: string[];
}

interface UseEmployeesOptions {
  tenantId: string;
  role: 'owner' | 'manager';
  locationIds?: string[]; // For manager filtering
}

export function useEmployees({ tenantId, role, locationIds }: UseEmployeesOptions) {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchEmployees = async () => {
    if (!tenantId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const usersRef = collection(firestore, 'users');
      const constraints: QueryConstraint[] = [
        where('tenantId', '==', tenantId),
        where('role', 'in', ['employee', 'manager'])
      ];

      const q = query(usersRef, ...constraints);
      const snapshot = await getDocs(q);

      let employeeData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Employee[];

      // Filter by location for managers
      if (role === 'manager' && locationIds && locationIds.length > 0) {
        employeeData = employeeData.filter(emp => 
          emp.locationIds?.some(locId => locationIds.includes(locId))
        );
      }

      setEmployees(employeeData);
    } catch (err: any) {
      console.error('Error fetching employees:', err);
      setError(err.message || 'Failed to fetch employees');
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, [tenantId, role, locationIds?.join(',')]);

  return {
    employees,
    loading,
    error,
    refetch: fetchEmployees
  };
}
